import { useQuery } from '@tanstack/react-query';
import type { ItemType } from '@brickloop/core/schemas';
import { api, qs } from '../api';
import { number } from '../ui';

interface Lookup {
  exists: boolean;
  qtyTotal?: number;
  locations: { id: number | null; code: string | null; qty: number }[];
}

/**
 * Booking stock in: if the same part, colour and condition is already on a shelf, say where and offer to put
 * the new pieces in the same bin. A bin that already holds it is the best place, so each is one tap.
 *
 * `chosen` is the bin picked on the row ('' when none). `onUse` picks one.
 */
export function ExistingBinAlert({
  itemType,
  itemNo,
  colorId,
  condition,
  chosen,
  onUse,
}: {
  itemType: ItemType;
  itemNo: string;
  colorId: number | undefined;
  condition: 'N' | 'U';
  chosen: string;
  onUse: (locationId: number) => void;
}) {
  const lookup = useQuery({
    queryKey: ['lot-lookup', itemNo, colorId, condition, itemType],
    enabled: !!itemNo && colorId !== undefined,
    queryFn: () => api<Lookup>(`/api/v1/lots/lookup${qs({ itemType, itemNo, colorId, condition })}`),
  });
  const d = lookup.data;
  if (!d?.exists) return null;
  const placed = d.locations.filter((l): l is { id: number; code: string | null; qty: number } => l.id !== null);
  if (placed.length === 0) {
    return <span className="text-slate-500">Already {number(d.qtyTotal ?? 0)} in stock, not in a bin yet.</span>;
  }
  const inChosen = chosen !== '' && placed.some((l) => String(l.id) === chosen);
  const elsewhere = chosen !== '' && !inChosen;
  return (
    <span
      role="status"
      className={`flex flex-wrap items-center gap-2 rounded-md px-2 py-1 ${inChosen ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'}`}
    >
      <span>
        {inChosen ? 'Good: ' : ''}
        Already {number(d.qtyTotal ?? 0)} in stock in {placed.map((l) => `${l.code ?? '?'} (${number(l.qty)})`).join(', ')}.
        {elsewhere ? ' You have chosen a different bin.' : !inChosen ? ' Put these in the same bin?' : ''}
      </span>
      {!inChosen &&
        placed.map((l) => (
          <button key={l.id} type="button" onClick={() => onUse(l.id)} className="rounded border border-amber-700 bg-white px-2 py-0.5 font-medium hover:bg-amber-100">
            Use {l.code}
          </button>
        ))}
    </span>
  );
}
